import L from 'leaflet'

/**
 * Cooperative map gestures without device detection:
 * - one touch: page scroll
 * - two touches: map pan/zoom
 * - mouse over map: single-button drag
 *
 * Adapted from Leaflet.GestureHandling:
 * https://github.com/elmarquis/Leaflet.GestureHandling
 */
export function setupCooperativeGestures(map: L.Map): () => void {
  const container = map.getContainer()
  const abortController = new AbortController()
  const { signal } = abortController

  let isDraggingMap = false
  let activeTouchCount = 0

  const disableMapPan = () => {
    map.dragging.disable()
  }

  const enableMapPan = () => {
    map.dragging.enable()
  }

  disableMapPan()

  const onMoveStart = () => {
    isDraggingMap = true
  }

  const onMoveEnd = () => {
    isDraggingMap = false
  }

  const handleTouch = (event: TouchEvent) => {
    activeTouchCount = event.touches.length

    if (event.type === 'touchend' || event.type === 'touchcancel') {
      if (activeTouchCount < 2) {
        disableMapPan()
      }
      return
    }

    if (event.type !== 'touchstart' && event.type !== 'touchmove') {
      return
    }

    if (event.touches.length === 1) {
      disableMapPan()
      return
    }

    event.preventDefault()
    enableMapPan()
  }

  const onMouseOver = () => {
    // Ignore synthetic mouse events while a touch gesture is active.
    if (activeTouchCount === 0) {
      enableMapPan()
    }
  }

  const onMouseOut = () => {
    if (!isDraggingMap) {
      disableMapPan()
    }
  }

  // Native touch listeners behave more reliably than Leaflet's wrappers on Android.
  container.addEventListener('touchstart', handleTouch, { passive: false, signal })
  container.addEventListener('touchmove', handleTouch, { passive: false, signal })
  container.addEventListener('touchend', handleTouch, { signal })
  container.addEventListener('touchcancel', handleTouch, { signal })

  map.on('mouseover', onMouseOver)
  map.on('mouseout', onMouseOut)
  map.on('movestart', onMoveStart)
  map.on('moveend', onMoveEnd)

  if (container.matches(':hover')) {
    enableMapPan()
  }

  return () => {
    abortController.abort()
    map.off('mouseover', onMouseOver)
    map.off('mouseout', onMouseOut)
    map.off('movestart', onMoveStart)
    map.off('moveend', onMoveEnd)
    enableMapPan()
  }
}
